/**
 * Keeps React alive when something rewrites the page's text under it.
 *
 * A page translator replaces each text node with
 * `<font><font>translated</font></font>`. React still holds the original
 * node, so the next render that inserts beside it or removes it calls
 * `insertBefore` / `removeChild` with a node that is no longer a child of the
 * parent — a `NotFoundError` that unmounts the whole screen (Sentry
 * OPTIVO-UI-A on the strategy editor, OPTIVO-UI-7/8 on Prices, each after a
 * click on a `font > font`). React will not handle it (facebook/react#11538).
 *
 * This is the backstop, not the fix. `translate="no"` in index.html keeps
 * Chrome's translator, and every translator that honours the attribute, off
 * the page: translated text stops updating when React changes it, which on a
 * pricing screen is a figure that is no longer true. The page opens in the
 * browser's language instead (`i18n/language-provider.tsx`). What reaches
 * this guard is an extension that translates or rewrites the page anyway.
 *
 * So the two methods tolerate exactly that case and nothing else: a node
 * that is a real child goes through untouched. A removal of a node that has
 * already left is a no-op. An insertion before a node that has moved inside
 * a wrapper goes before the wrapper — the same place on screen — and one
 * before a node that has left altogether is appended rather than lost. The
 * rewritten text may read stale until the page is reloaded; the alternative
 * is a blank page.
 */
export function installTranslationDomGuard(): () => void {
  if (typeof Node === "undefined") return () => undefined;

  const proto = Node.prototype;
  const nativeRemoveChild = proto.removeChild;
  const nativeInsertBefore = proto.insertBefore;

  proto.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    return nativeRemoveChild.call(this, child) as T;
  };

  proto.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    reference: Node | null,
  ): T {
    if (reference == null || reference.parentNode === this) {
      return nativeInsertBefore.call(this, node, reference) as T;
    }
    let anchor: Node | null = reference;
    while (anchor != null && anchor.parentNode !== this) {
      anchor = anchor.parentNode;
    }
    return nativeInsertBefore.call(this, node, anchor) as T;
  };

  return () => {
    proto.removeChild = nativeRemoveChild;
    proto.insertBefore = nativeInsertBefore;
  };
}
